var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link, Stack, Switch, TableBody, TableCell, TableRow, TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DatePicker, FormField } from '../../common';
import { WizardTable } from '../common/WizardTable';
import { MAX_TEXT_LENGTH } from '../constants';
import { groupHolidaysByName, useRegionHolidaysAPI } from './selectors';
var LoadingBox = styled(Box)(function (_a) {
  var theme = _a.theme;
  return {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  };
});
var AddHolidayPanel = styled(Box)(function (_a) {
  var theme = _a.theme;
  return {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.primary.light,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2)
  };
});
/** Holidays recur every year, so we show the month and day without a year (e.g. "January 1"). */
var formatHolidayDate = function (dateStr, locale) {
  var date = new Date("".concat(dateStr, "T00:00:00"));
  return date.toLocaleDateString(locale, {
    month: 'long',
    day: 'numeric'
  });
};
export var SelectHolidaysModal = function (_a) {
  var open = _a.open,
    regionName = _a.regionName,
    countryCode = _a.countryCode,
    regionCode = _a.regionCode,
    preSelectedHolidays = _a.preSelectedHolidays,
    _b = _a.preSelectAll,
    preSelectAll = _b === void 0 ? false : _b,
    onClose = _a.onClose,
    onSave = _a.onSave;
  var _c = useTranslation('orgConfig', {
      keyPrefix: 'hours'
    }),
    i18n = _c.i18n,
    t = _c.t;
  var tAction = useTranslation('translation', {
    keyPrefix: 'action'
  }).t;
  var _d = useRegionHolidaysAPI(countryCode, regionCode),
    holidaysData = _d[0],
    fetchHolidays = _d[1];
  var _e = useState(new Set(preSelectedHolidays.map(function (h) {
      return h.name;
    }))),
    selected = _e[0],
    setSelected = _e[1];
  var _f = useState([]),
    customHolidays = _f[0],
    setCustomHolidays = _f[1];
  var _g = useState(''),
    customName = _g[0],
    setCustomName = _g[1];
  // Form cục bộ chỉ để chạy field customDate qua common DatePicker (component này cần control để track lỗi)
  var _h = useForm({
      defaultValues: {
        customDate: null
      }
    }),
    dateControl = _h.control,
    watchDate = _h.watch,
    resetDateForm = _h.reset;
  var customDate = watchDate('customDate');
  useEffect(function () {
    if (open && countryCode) {
      fetchHolidays();
    }
  }, [open, countryCode, regionCode, fetchHolidays]);
  var holidays = useMemo(function () {
    var _a;
    return (_a = holidaysData.data) !== null && _a !== void 0 ? _a : [];
  }, [holidaysData.data]);
  var wasOpenRef = useRef(false);
  var selectionInitializedRef = useRef(false);
  useEffect(function () {
    var justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!open) {
      selectionInitializedRef.current = false;
      return;
    }
    if (justOpened) {
      setCustomHolidays([]);
      setCustomName('');
      resetDateForm({
        customDate: null
      });
      selectionInitializedRef.current = false;
      setSelected(new Set(preSelectedHolidays.map(function (h) {
        return h.name;
      })));
    }
    if (selectionInitializedRef.current) return;
    if (holidays.length === 0) return;
    var libraryNames = new Set(holidays.map(function (h) {
      return h.name;
    }));
    if (preSelectAll && preSelectedHolidays.length === 0) {
      setSelected(new Set(holidays.map(function (h) {
        return h.name;
      })));
    } else {
      var customOnes = preSelectedHolidays.filter(function (h) {
        return !libraryNames.has(h.name);
      });
      if (customOnes.length > 0) {
        setCustomHolidays(customOnes);
      }
    }
    selectionInitializedRef.current = true;
  }, [open, preSelectAll, preSelectedHolidays, holidays, resetDateForm]);
  var groups = useMemo(function () {
    return groupHolidaysByName(__spreadArray(__spreadArray([], holidays, true), customHolidays, true));
  }, [holidays, customHolidays]);
  var toggleHoliday = function (name) {
    selectionInitializedRef.current = true;
    setSelected(function (prev) {
      var next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };
  var setAllSelected = function (value) {
    selectionInitializedRef.current = true;
    setSelected(value ? new Set(groups.map(function (g) {
      return g.key;
    })) : new Set());
  };
  var isDuplicateName = !!customName.trim() && groups.some(function (g) {
    return g.name.toLowerCase() === customName.trim().toLowerCase();
  });
  var handleAddCustom = function () {
    if (!customName.trim() || !(customDate === null || customDate === void 0 ? void 0 : customDate.isValid()) || isDuplicateName) return;
    selectionInitializedRef.current = true;
    var name = customName.trim();
    var entry = {
      date: customDate.format('YYYY-MM-DD'),
      name: name
    };
    setCustomHolidays(function (prev) {
      return __spreadArray(__spreadArray([], prev, true), [entry], false);
    });
    setSelected(function (prev) {
      return new Set(__spreadArray(__spreadArray([], Array.from(prev), true), [name], false));
    });
    setCustomName('');
    resetDateForm({
      customDate: null
    });
  };
  var handleSave = function () {
    var allHolidays = __spreadArray(__spreadArray([], holidays, true), customHolidays, true);
    var selectedHolidays = allHolidays.filter(function (h) {
      return selected.has(h.name);
    });
    onSave(selectedHolidays);
    onClose();
  };
  return _jsxs(Dialog, __assign({
    open: open,
    onClose: onClose,
    maxWidth: "md",
    fullWidth: true
  }, {
    children: [_jsx(DialogTitle, __assign({
      sx: {
        p: 6,
        pb: 3
      }
    }, {
      children: _jsxs(Stack, __assign({
        direction: "row",
        alignItems: "center",
        justifyContent: "space-between",
        spacing: 2
      }, {
        children: [_jsx(Typography, __assign({
          variant: "h3",
          fontWeight: "bold"
        }, {
          children: t('holidaysModal.title')
        })), !holidaysData.requesting && groups.length > 0 && _jsxs(Stack, __assign({
          direction: "row",
          alignItems: "center",
          spacing: 2
        }, {
          children: [_jsx(Link, __assign({
            component: "button",
            variant: "body2",
            underline: "always",
            onClick: function () {
              return setAllSelected(true);
            }
          }, {
            children: t('holidaysModal.selectAll')
          })), _jsx(Typography, __assign({
            color: "text.disabled",
            variant: "body2"
          }, {
            children: "|"
          })), _jsx(Link, __assign({
            component: "button",
            variant: "body2",
            underline: "always",
            onClick: function () {
              return setAllSelected(false);
            }
          }, {
            children: t('holidaysModal.deselectAll')
          }))]
        }))]
      }))
    })), _jsxs(DialogContent, __assign({
      sx: {
        px: 6,
        pb: 3,
        pt: 0
      }
    }, {
      children: [holidaysData.requesting && _jsx(LoadingBox, {
        children: _jsx(CircularProgress, {
          size: 32
        })
      }), !holidaysData.requesting && _jsxs(_Fragment, {
        children: [_jsx(WizardTable, __assign({
          stickyHeader: true,
          containerSx: {
            maxHeight: 'min(65vh, 560px)',
            overflow: 'auto'
          },
          headers: _jsxs(_Fragment, {
            children: [_jsx(TableCell, {
              children: regionName
            }), _jsx(TableCell, {
              sx: {
                width: '12rem'
              }
            }), _jsx(TableCell, {
              sx: {
                width: '5rem'
              }
            })]
          })
        }, {
          children: _jsxs(TableBody, {
            children: [groups.length === 0 && _jsx(TableRow, {
              children: _jsx(TableCell, __assign({
                colSpan: 3
              }, {
                children: _jsx(Typography, __assign({
                  color: "text.secondary",
                  variant: "body2",
                  sx: {
                    py: 1
                  }
                }, {
                  children: t('holidaysModal.noHolidays')
                }))
              }))
            }), groups.map(function (g) {
              return _jsxs(TableRow, __assign({
                hover: true,
                sx: {
                  cursor: 'pointer'
                },
                onClick: function () {
                  return toggleHoliday(g.key);
                }
              }, {
                children: [_jsx(TableCell, {
                  children: _jsx(Typography, __assign({
                    variant: "body2"
                  }, {
                    children: g.name
                  }))
                }), _jsx(TableCell, __assign({
                  sx: {
                    width: '12rem'
                  }
                }, {
                  children: _jsx(Typography, __assign({
                    variant: "body2"
                  }, {
                    children: formatHolidayDate(g.displayDate, i18n.language)
                  }))
                })), _jsx(TableCell, __assign({
                  align: "center",
                  sx: {
                    width: '4rem'
                  }
                }, {
                  children: _jsx(Switch, {
                    size: "small",
                    checked: selected.has(g.key),
                    onChange: function () {
                      return toggleHoliday(g.key);
                    },
                    onClick: function (e) {
                      return e.stopPropagation();
                    }
                  })
                }))]
              }), g.key);
            })]
          })
        })), _jsx(AddHolidayPanel, {
          children: _jsxs(Stack, __assign({
            direction: "row",
            spacing: 2,
            alignItems: "flex-start"
          }, {
            children: [_jsx(TextField, {
              size: "small",
              placeholder: t('holidaysModal.customPlaceholder'),
              value: customName,
              onChange: function (e) {
                return setCustomName(e.target.value);
              },
              fullWidth: true,
              variant: "outlined",
              error: isDuplicateName,
              inputProps: {
                maxLength: MAX_TEXT_LENGTH
              }
            }), _jsx(FormField, __assign({
              name: "customDate",
              control: dateControl
            }, {
              children: _jsx(DatePicker, {
                control: dateControl,
                sx: {
                  minWidth: '12rem'
                }
              })
            })), _jsx(Button, __assign({
              variant: "outlined",
              onClick: handleAddCustom,
              disabled: !customName.trim() || !(customDate === null || customDate === void 0 ? void 0 : customDate.isValid()) || isDuplicateName,
              sx: {
                flexShrink: 0
              }
            }, {
              children: tAction('add')
            }))]
          }))
        })]
      }), isDuplicateName && _jsx(Typography, __assign({
        variant: "caption",
        color: "error",
        sx: {
          display: 'block'
        }
      }, {
        children: t('holidaysModal.duplicateName')
      }))]
    })), _jsxs(DialogActions, {
      children: [_jsx(Button, __assign({
        variant: "outlined",
        onClick: onClose
      }, {
        children: tAction('cancel')
      })), _jsx(Button, __assign({
        variant: "contained",
        onClick: handleSave,
        disabled: holidaysData.requesting === true
      }, {
        children: tAction('save')
      }))]
    })]
  }));
};